import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  createContext,
  use,
  useEffect,
  useId,
  useMemo,
  useState,
  useSyncExternalStore,
  type PropsWithChildren,
} from 'react'

import {useMergeMethodContext} from '../contexts/MergeMethodContext'
import {EffectiveEntryStatus} from '../helpers/effective-entry-status'
import {getStackCondition} from '../helpers/json-api-helpers'
import type {StackSectionStackEntry} from '../helpers/merge-box-status-calculator/stack-section-types'
import {useStackRebaseStatus} from '../hooks/mutations/use-stack-rebase-mutation'
import {
  useStackedPullHasUnknownMergeabilityTelemetry,
  useUnstackedPullHasUnknownMergeabilityTelemetry,
  type UnknownMergeabilityStackEntry,
} from '../hooks/use-unknown-mergeability-telemetry'
import {useCachedMergeBoxPageData} from '../page-data/loaders/use-merge-box-page-data'

type MountedStackEntries = {stackId: number; entries: UnknownMergeabilityStackEntry[]}

const ANALYTICS_METADATA = {}

// Stack entries shared by every mounted MergeBox. The inline merge box and the merge status panel can be mounted at
// the same time, so their entries are combined here and timed once.
class MountedStackEntriesStore {
  #byMergeBox = new Map<string, MountedStackEntries>()
  #snapshot: MountedStackEntries[] = []
  #listeners = new Set<() => void>()

  subscribe = (listener: () => void) => {
    this.#listeners.add(listener)
    return () => {
      this.#listeners.delete(listener)
    }
  }

  getSnapshot = () => this.#snapshot

  set(mergeBoxId: string, mountedStackEntries: MountedStackEntries | undefined) {
    if (mountedStackEntries) {
      this.#byMergeBox.set(mergeBoxId, mountedStackEntries)
    } else if (!this.#byMergeBox.delete(mergeBoxId)) {
      return
    }
    this.#snapshot = [...this.#byMergeBox.values()]
    for (const listener of this.#listeners) listener()
  }
}

const MountedStackEntriesContext = createContext<MountedStackEntriesStore | null>(null)

function isTelemetryDisabled() {
  return isFeatureEnabled('mergebox_unknown_mergeability_telemetry_disabled')
}

/**
 * Times how long the pull request's mergeability stays unknown, once for the whole page. Render it above every merge
 * box and merge status button for the pull request, inside `PageDataContextProvider` and `MergeMethodContextProvider`.
 *
 * The current pull request is timed from the cached `merge_box` response, so it's tracked wherever another component
 * has loaded that response, even when no merge box is mounted. Other stack entries need the `stack_mergeability`
 * responses that only a mounted MergeBox loads, so they're only timed while one is mounted.
 *
 * The `mergebox_unknown_mergeability_telemetry_disabled` feature flag turns all of this off.
 */
export function UnknownMergeabilityTelemetryProvider({children}: PropsWithChildren) {
  const [store] = useState(() => new MountedStackEntriesStore())

  return (
    <MountedStackEntriesContext value={store}>
      {children}
      {!isTelemetryDisabled() && (
        <AnalyticsProvider
          appName="pull_request"
          category="mergebox_unknown_mergeability"
          metadata={ANALYTICS_METADATA}
        >
          <ReportUnknownMergeability store={store} />
        </AnalyticsProvider>
      )}
    </MountedStackEntriesContext>
  )
}

/**
 * Shares a mounted MergeBox's stack entries, other than the current pull request, with
 * `UnknownMergeabilityTelemetryProvider`. Does nothing outside the provider.
 */
export function useShareStackEntriesWithUnknownMergeabilityTelemetry({
  stackId,
  stackEntries,
}: {
  stackId: number | undefined
  stackEntries: StackSectionStackEntry[]
}) {
  const store = use(MountedStackEntriesContext)
  const mergeBoxId = useId()
  const isDisabled = isTelemetryDisabled()
  const otherEntries = stackEntries.filter(entry => entry.position !== 'CURRENT')
  // Joined so the shared entries only change when the entries do, not on every render.
  const members = otherEntries.map(entry => entry.pull.number).join(',')
  const unknown = otherEntries
    .filter(entry => entry.effectiveEntryStatus === EffectiveEntryStatus.Loading)
    .map(entry => entry.pull.number)
    .join(',')

  const sharedStackEntries = useMemo((): MountedStackEntries | undefined => {
    if (isDisabled || stackId === undefined) return undefined
    const unknownNumbers = new Set(splitNumbers(unknown))
    return {
      stackId,
      entries: splitNumbers(members).map(pullRequestNumber => ({
        pullRequestNumber,
        isUnknown: unknownNumbers.has(pullRequestNumber),
      })),
    }
  }, [isDisabled, stackId, members, unknown])

  useEffect(() => {
    store?.set(mergeBoxId, sharedStackEntries)
  }, [store, mergeBoxId, sharedStackEntries])

  // Removed only on unmount, so updating the entries never briefly drops them and their timers.
  useEffect(() => () => store?.set(mergeBoxId, undefined), [store, mergeBoxId])
}

function ReportUnknownMergeability({store}: {store: MountedStackEntriesStore}) {
  const {mergeMethod} = useMergeMethodContext()
  const {data} = useCachedMergeBoxPageData({mergeMethod})
  const mountedStackEntries = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)
  const {isRebasing} = useStackRebaseStatus()

  const mergeRequirements = data?.mergeRequirements ?? null
  const stackCondition = getStackCondition(mergeRequirements)
  const isStacked = !!stackCondition?.isStacked
  const stack = isStacked ? stackCondition?.stack : undefined
  const currentPullRequestNumber = stackCondition?.entries.find(entry => entry.position === 'CURRENT')?.pull.number

  useUnstackedPullHasUnknownMergeabilityTelemetry({
    pullRequestId: data?.pullRequest.id,
    mergeRequirementsState: mergeRequirements?.state,
    isStacked,
  })
  useStackedPullHasUnknownMergeabilityTelemetry({
    stackMetadata: stack ? {id: stack.id, number: stack.number, created_at: stack.createdAt} : undefined,
    entries: stack
      ? combineStackEntries({
          stackId: stack.id,
          currentPullRequestNumber,
          isCurrentUnknown: mergeRequirements?.state === 'UNKNOWN',
          mountedStackEntries,
        })
      : [],
    isRebasing,
  })

  return null
}

function combineStackEntries({
  stackId,
  currentPullRequestNumber,
  isCurrentUnknown,
  mountedStackEntries,
}: {
  stackId: number
  currentPullRequestNumber: number | undefined
  isCurrentUnknown: boolean
  mountedStackEntries: MountedStackEntries[]
}): UnknownMergeabilityStackEntry[] {
  const isUnknownByNumber = new Map<number, boolean>()
  if (currentPullRequestNumber !== undefined) isUnknownByNumber.set(currentPullRequestNumber, isCurrentUnknown)

  for (const mounted of mountedStackEntries) {
    if (mounted.stackId !== stackId) continue
    for (const {pullRequestNumber, isUnknown} of mounted.entries) {
      if (pullRequestNumber === currentPullRequestNumber) continue
      isUnknownByNumber.set(pullRequestNumber, (isUnknownByNumber.get(pullRequestNumber) ?? false) || isUnknown)
    }
  }

  return [...isUnknownByNumber].map(([pullRequestNumber, isUnknown]) => ({pullRequestNumber, isUnknown}))
}

function splitNumbers(value: string): number[] {
  return value ? value.split(',').map(Number) : []
}
